import cn from 'classnames'
import React, { forwardRef, memo, useMemo } from 'react'
import { shallowEqual } from 'react-redux'

import { isBodyCell, isBodyRow, isHeaderCell, isHeaderRow } from './helpers'
import {
  ISimpleTableBodyCellProps,
  ISimpleTableBodyRowProps,
  ISimpleTableHeaderCellProps,
  ISimpleTableHeaderRowProps,
  ISimpleTablePaginationProps,
  ISimpleTableProps,
} from './models'

type ISimpleTableComponent = React.ForwardRefExoticComponent<
  React.PropsWithoutRef<ISimpleTableProps> & React.RefAttributes<HTMLTableElement>
> & {
  HeaderRow: typeof SimpleTableHeaderRow
  HeaderCell: typeof SimpleTableHeaderCell
  BodyRow: typeof SimpleTableBodyRow
  BodyCell: typeof SimpleTableBodyCell
  Pagination: typeof SimpleTablePagination
}

export const SimpleTable = memo<ISimpleTableProps>(
  forwardRef<HTMLTableElement, ISimpleTableProps>(
    ({ children, classes, placeholderColspan, ...attr }, ref) => {
      const tHeadRow = useMemo(
        () => React.Children.toArray(children).filter(isHeaderRow),
        [children]
      )

      const colSpan = useMemo(() => {
        if (tHeadRow.length === 0) return 0

        if (tHeadRow[0]?.props?.children === undefined) return 0

        const headerCells = React.Children.toArray(tHeadRow[0].props.children).filter(isHeaderCell)
        return headerCells.length
      }, [tHeadRow])

      const tBodyRow = useMemo(() => {
        const row = React.Children.toArray(children).filter(isBodyRow)
        if (row && row.length) {
          return row
        }

        return (
          <SimpleTableBodyRow>
            <SimpleTableBodyCell
              colSpan={placeholderColspan || colSpan}
              style={{ cursor: 'default', pointerEvents: 'none' }}
            >
              No data
            </SimpleTableBodyCell>
          </SimpleTableBodyRow>
        )
      }, [children, colSpan, placeholderColspan])

      return (
        <table {...attr} ref={ref}>
          <thead className={classes?.thead}>{tHeadRow}</thead>
          <tbody className={classes?.tbody}>{tBodyRow}</tbody>
        </table>
      )
    }
  ),
  shallowEqual
) as ISimpleTableComponent

const SimpleTableHeaderRow = memo<ISimpleTableHeaderRowProps>(
  ({ children, ...attr }: ISimpleTableHeaderRowProps) => {
    const headerCells = React.Children.toArray(children).filter(isHeaderCell)

    return <tr {...attr}>{headerCells}</tr>
  }
)

const SimpleTableHeaderCell = memo<ISimpleTableHeaderCellProps>(({ children, ...attr }) => {
  return <th {...attr}>{children}</th>
})

const SimpleTableBodyRow = memo<ISimpleTableBodyRowProps>(({ children, ...attr }) => {
  const bodyCells = React.Children.toArray(children).filter(
    (child) => isBodyCell(child) || isHeaderCell(child)
  )

  return <tr {...attr}>{bodyCells}</tr>
})

const SimpleTableBodyCell = memo<ISimpleTableBodyCellProps>(({ children, ...attr }) => {
  return <td {...attr}>{children}</td>
})

const SimpleTablePagination = memo<ISimpleTablePaginationProps>(
  ({ page, pageSize, total, classes, onPageChange, isDisabled, ...attr }) => {
    const numberOfPages = total <= pageSize ? 1 : Math.ceil(total / pageSize)

    const handlePageChange = (newPage: number) => {
      if (newPage < 0 || newPage >= numberOfPages) return
      onPageChange(newPage)
    }

    if (numberOfPages <= 1) return null

    if (numberOfPages <= 5) {
      return (
        <div className={cn(attr.className, classes?.container)} {...attr}>
          {Array.from({ length: numberOfPages }, (_, index) => (
            <button
              className={classes?.button}
              key={index}
              onClick={() => handlePageChange(index)}
              disabled={page === index || isDisabled}
              data-selected={page === index}
            >
              {index + 1}
            </button>
          ))}
        </div>
      )
    }

    return (
      <div className={cn(attr.className, classes?.container)} {...attr}>
        <button
          className={classes?.button}
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 0}
        >
          <i className="kbase kbase-chevron-left" />
        </button>
        {Array.from({ length: numberOfPages }, (_, index) => {
          if (index === 0 || index === numberOfPages - 1 || Math.abs(index - page) < 2) {
            return (
              <button
                className={classes?.button}
                key={index}
                onClick={() => handlePageChange(index)}
                disabled={page === index || isDisabled}
                data-selected={page === index}
              >
                {index + 1}
              </button>
            )
          }

          if (index === 1 || index === numberOfPages - 2) {
            return (
              <span className={classes?.button} key={index}>
                ...
              </span>
            )
          }

          return null
        })}
        <button
          className={classes?.button}
          onClick={() => handlePageChange(page + 1)}
          disabled={isDisabled || page === numberOfPages - 1}
        >
          <i className="kbase kbase-chevron-right" />
        </button>
      </div>
    )
  }
)

SimpleTable.displayName = 'SimpleTable'
SimpleTableHeaderRow.displayName = 'SimpleTableHeaderRow'
SimpleTableHeaderCell.displayName = 'SimpleTableHeaderCell'
SimpleTableBodyRow.displayName = 'SimpleTableBodyRow'
SimpleTableBodyCell.displayName = 'SimpleTableBodyCell'
SimpleTablePagination.displayName = 'SimpleTablePagination'

SimpleTable.HeaderCell = SimpleTableHeaderCell
SimpleTable.BodyCell = SimpleTableBodyCell
SimpleTable.HeaderRow = SimpleTableHeaderRow
SimpleTable.BodyRow = SimpleTableBodyRow
SimpleTable.Pagination = SimpleTablePagination
